import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  Icon,
  Input,
  Link,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  FrameworkAgreementDto,
  FrameworkAgreementPatchDto,
  PanelMemberDto,
  PanelStatus,
  SupplierAdditionRefusalDto,
} from "../../autogen/bff-api";
import { coordinatorRegistry, useEditor } from "../../common/editing/save-coordinator";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { urls } from "../../urls";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { AddFrameworkSupplierModal } from "./AddFrameworkSupplierModal";
import { activePanel } from "./callOffAvailability";
import type { PanelEdits } from "./panelEdits";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { RowTitle } from "./RowTitle";
import { SectionCard } from "./SectionCard";

export const PanelSection = ({
  frameworkAgreementId,
  canEdit,
  panelEdits,
}: {
  frameworkAgreementId: string;
  canEdit: boolean;
  panelEdits: PanelEdits;
}) => {
  const { t } = useTranslation();
  const { state, updateField, entityStatus } = useEditor<FrameworkAgreementDto, FrameworkAgreementPatchDto>(
    frameworkAgreementId,
  );
  const isSaving = entityStatus === "pending" || entityStatus === "saving";

  const panel = state.panel ?? [];
  const unregistered = state.unregisteredSuppliers ?? [];
  const names = useRef(new Map<string, string>());
  panel.forEach((member) => {
    if (member.name) names.current.set(member.organizationId, member.name);
  });
  const nameOf = (member: PanelMemberDto) => member.name || names.current.get(member.organizationId) || "";
  const activeRankings = activePanel(state)
    .map((member) => member.ranking)
    .filter((ranking): ranking is number => ranking != null);
  const hasTiedRanking = new Set(activeRankings).size !== activeRankings.length;

  const toast = useToast();
  const { show: confirm, modal: confirmationModal } = useConfirmationModal();
  const savedMessage = useRef<string | null>(null);
  const previousStatus = useRef(entityStatus);
  const [reverts, setReverts] = useState(0);
  const [isAdding, setAdding] = useState(false);

  useEffect(() => {
    const before = previousStatus.current;
    previousStatus.current = entityStatus;
    if (savedMessage.current === null || before === entityStatus) return;
    if (entityStatus === "saved") {
      toast({ variant: "subtle", status: "success", title: savedMessage.current });
      savedMessage.current = null;
    } else if (entityStatus === "error") {
      savedMessage.current = null;
      coordinatorRegistry.get(frameworkAgreementId)?.discardPendingChanges();
      panelEdits.discard();
      setReverts((count) => count + 1);
    }
  }, [entityStatus, frameworkAgreementId, panelEdits, toast]);

  const changeRanking = (member: PanelMemberDto, ranking: number | null) => {
    savedMessage.current = t("{{name}}'s ranking is saved.", { name: nameOf(member) });
    patchMember(member, { ranking });
  };

  const changeStatus = (member: PanelMemberDto, status: PanelStatus) => {
    if (status === PanelStatus.Active) {
      savedMessage.current = t("{{name}} is active again.", { name: nameOf(member) });
      patchMember(member, { status });
      return;
    }
    confirm({
      title: t("Suspend {{name}}?", { name: nameOf(member) }),
      description: t(
        "The supplier gets no direct call-offs and isn't invited to new mini-competitions until it is made active again.",
      ),
      confirmButtonText: t("Suspend") ?? undefined,
      confirmButtonColor: "red",
      size: "lg",
      onConfirmed: () => {
        savedMessage.current = t("{{name}} is suspended.", { name: nameOf(member) });
        patchMember(member, { status });
      },
    });
  };

  const patchMember = (member: PanelMemberDto, change: { ranking?: number | null; status?: PanelStatus }) => {
    panelEdits.record(member.organizationId, change);
    updateField(
      {
        panel: panel.map((row) =>
          row.organizationId === member.organizationId
            ? { organizationId: row.organizationId, ranking: row.ranking ?? null, status: row.status, ...change }
            : { organizationId: row.organizationId, ranking: row.ranking ?? null, status: row.status },
        ),
      } as Partial<FrameworkAgreementPatchDto>,
      { debounceMs: change.ranking === undefined ? 0 : 400 },
    );
  };

  return (
    <Stack spacing="3">
      {confirmationModal}
      {isAdding && <AddFrameworkSupplierModal framework={state} onClose={() => setAdding(false)} />}
      <SectionCard
        title={t("Suppliers on the framework agreement")}
        action={
          state.mayAddSuppliers ? (
            <Button size="sm" colorScheme="teal" onClick={() => setAdding(true)}>
              {t("Add supplier")}
            </Button>
          ) : state.mayManage && state.supplierAdditionRefusal === SupplierAdditionRefusalDto.NotInPlan ? (
            <Explanation text={t("NotInPlan")} shouldWrapChildren>
              <Button size="sm" colorScheme="teal" isDisabled>
                {t("Add supplier")}
              </Button>
            </Explanation>
          ) : (
            state.mayManage &&
            state.supplierAdditionRefusal === SupplierAdditionRefusalDto.PanelFull && (
              <Text fontSize="sm" color="smMuted">
                {t("The framework agreement allows at most {{count}} suppliers.", { count: state.maxParticipants ?? 0 })}
              </Text>
            )
          )
        }
        note={
          isSaving && (
            <Text fontSize="sm" color="smMuted">
              {t("Saving…")}
            </Text>
          )
        }
      >
        {panel.length === 0 && unregistered.length === 0 ? (
          <Center py="16" px="6" flexDirection="column" gap="3" color="smMuted">
            <Icon as={FiUsers} boxSize="28px" />
            <Text fontWeight="medium" color="smText">
              {t("No suppliers have been added to this framework agreement yet")}
            </Text>
            <Text fontSize="sm" maxW="420px" textAlign="center">
              {!state.sourcingEventId
                ? t("Add the suppliers that are parties to this framework agreement.")
                : t("Suppliers join the panel when the competition behind this framework agreement is awarded.")}
            </Text>
          </Center>
        ) : (
          <HorizontalScrollBox>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Supplier")}</Th>
                  <Th width="140px">{t("Ranking")}</Th>
                  <Th width="200px" minWidth="36">{t("Status")}</Th>
                  <Th>{t("Contract")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {panel.map((member) => (
                  <Tr key={member.organizationId}>
                    <Td fontWeight="semibold">
                      <RowTitle>{nameOf(member)}</RowTitle>
                    </Td>
                    <Td>
                      {canEdit ? (
                        <RankingInput
                          key={reverts}
                          ranking={member.ranking ?? null}
                          panelSize={panel.length}
                          isDisabled={false}
                          onRankingChange={(ranking) => changeRanking(member, ranking)}
                        />
                      ) : (
                        <Text>{member.ranking ?? "-"}</Text>
                      )}
                    </Td>
                    <Td>
                      {canEdit ? (
                        <Select
                          size="sm"
                          aria-label={t("Status") ?? "Status"}
                          value={member.status}
                          onChange={(e) => changeStatus(member, e.target.value as PanelStatus)}
                        >
                          <option value={PanelStatus.Active}>{t("Active")}</option>
                          <option value={PanelStatus.Suspended}>{t("Suspended")}</option>
                        </Select>
                      ) : (
                        <Text>{member.status === PanelStatus.Active ? t("Active") : t("Suspended")}</Text>
                      )}
                    </Td>
                    <Td>
                      {member.contractId ? (
                        <Link as={RouterLink} to={urls.app.contracts.view.go(member.contractId)} color="smPrimary">
                          {t("Go to contract")}
                        </Link>
                      ) : (
                        <Text color="smMuted">-</Text>
                      )}
                      {member.contractEnded && (
                        <Text fontSize="sm" color="smMuted">
                          {t("Contract ended, so no call-offs or invitations")}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
                {unregistered.map((member) => (
                  <Tr key={member.organizationEntryId}>
                    <Td fontWeight="semibold">
                      <RowTitle>{member.name}</RowTitle>
                      <Text fontSize="sm" fontWeight="normal" color="smMuted">
                        {t("Not registered in SourceMagnet")}
                      </Text>
                    </Td>
                    <Td>
                      <Text color="smMuted">-</Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="smMuted">
                        {t("Invited to mini-competitions by e-mail")}
                      </Text>
                    </Td>
                    <Td>
                      {member.contractId ? (
                        <Link as={RouterLink} to={urls.app.contracts.view.go(member.contractId)} color="smPrimary">
                          {t("Go to contract")}
                        </Link>
                      ) : (
                        <Text color="smMuted">-</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </HorizontalScrollBox>
        )}
      </SectionCard>
      {hasTiedRanking && (
        <Alert status="warning" rounded="md">
          <AlertIcon />
          {t("Suppliers share a ranking. A direct call-off asks you to choose which of them it goes to.")}
        </Alert>
      )}
    </Stack>
  );
};

export const parseRanking = (text: string, panelSize: number): { isValid: boolean; ranking: number | null } => {
  const trimmed = text.trim();
  if (trimmed === "") return { isValid: true, ranking: null };
  const ranking = Number(trimmed);
  return /^[1-9]\d*$/.test(trimmed) && ranking <= panelSize
    ? { isValid: true, ranking }
    : { isValid: false, ranking: null };
};

export const RankingInput = ({
  ranking,
  panelSize,
  isDisabled,
  onRankingChange,
}: {
  ranking: number | null;
  panelSize: number;
  isDisabled: boolean;
  onRankingChange: (ranking: number | null) => void;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(ranking == null ? "" : String(ranking));
  const { isValid } = parseRanking(text, panelSize);

  const commit = () => {
    const parsed = parseRanking(text, panelSize);
    if (parsed.isValid && parsed.ranking !== ranking) onRankingChange(parsed.ranking);
  };

  return (
    <FormControl isInvalid={!isValid}>
      <Input
        size="sm"
        maxW="20"
        inputMode="numeric"
        aria-label={`${t("Ranking")}`}
        isDisabled={isDisabled}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      <FormErrorMessage>{t("A whole number from 1 to {{max}}", { max: panelSize })}</FormErrorMessage>
    </FormControl>
  );
};
